import React, { useState, useEffect } from 'react';
import LocationDropdown from '../../Dropdown/LocationName';
import AreaDropdown from '../../Dropdown/AreaDropdown';
import MedicalDropdown from '../../Dropdown/MedicalDropdown';

const EquipmentAddModal = ({
  show,
  onClose,
  formData,
  onChange,
  onLocationChange,
  onAreaChange,
  onSave,
  isEditMode,
  isLoading,
  userId,
}) => {
  const [selectionType, setSelectionType] = useState('location');
  const [errors, setErrors] = useState({});
  const [isAreaValid, setIsAreaValid] = useState(false);

  // Session Data
  const sessionRoleName = sessionStorage.getItem('Role_Name') || '';
  const sessionMedicalCenterId = sessionStorage.getItem('Medical_Center_Id') || '0';
  const sessionMedicalCenterName = sessionStorage.getItem('Medical_Center_Name') || '';

  const isResourceManager = sessionRoleName === 'EMS Resource Manager';

  // Condition 1: Force Medical Center only (no location option)
  const forceMedicalCenterOnly =
    isResourceManager &&
    sessionMedicalCenterId === '0' &&
    (!sessionMedicalCenterName || sessionMedicalCenterName.trim() === '');

  // Condition 2: Manager belongs to a specific medical center
  const isSpecificMedicalCenter =
    isResourceManager &&
    sessionMedicalCenterId &&
    sessionMedicalCenterId !== '0' &&
    sessionMedicalCenterId !== '[]' &&
    sessionMedicalCenterId !== '';

  // Auto-configure selection type when modal opens
  useEffect(() => {
    if (!show) return;

    if (forceMedicalCenterOnly) {
      setSelectionType('medical');
      onChange({ target: { name: 'location_id', value: '' } });
      onChange({ target: { name: 'area_id', value: '' } });
    } else if (isSpecificMedicalCenter) {
      setSelectionType('medical');
      onChange({ target: { name: 'medical_center_id', value: sessionMedicalCenterId } });
      onChange({ target: { name: 'location_id', value: '' } });
      onChange({ target: { name: 'area_id', value: '' } });
    } else if (formData.medical_center_id && formData.medical_center_id !== '0') {
      setSelectionType('medical');
    } else if (formData.location_id && formData.location_id !== '0') {
      setSelectionType('location');
    } else {
      setSelectionType('location');
    }

    // Reset errors and area validity
    setErrors({});
    setIsAreaValid(!!(formData.location_id && formData.area_id));
  }, [
    show,
    forceMedicalCenterOnly,
    isSpecificMedicalCenter,
    formData.medical_center_id,
    formData.location_id,
    formData.area_id,
  ]);

  const handleSelectionTypeChange = (e) => {
    const type = e.target.value;

    // Block switching to location if forced to medical center
    if ((forceMedicalCenterOnly || isSpecificMedicalCenter) && type === 'location') {
      return;
    }

    setSelectionType(type);

    if (type === 'location') {
      onChange({ target: { name: 'medical_center_id', value: '' } });
    } else {
      onChange({ target: { name: 'location_id', value: '' } });
      onChange({ target: { name: 'area_id', value: '' } });
      setIsAreaValid(false);
    }

    setErrors((prev) => ({
      ...prev,
      location_id: '',
      area_id: '',
      medical_center_id: '',
    }));
  };

  const handleLocationChange = (e) => {
    const locationId = e.target.value;
    onLocationChange?.(locationId);
    onChange({ target: { name: 'area_id', value: '' } });
    setIsAreaValid(false);

    setErrors((prev) => ({
      ...prev,
      location_id: locationId ? '' : 'Please select a location',
      area_id: locationId ? prev.area_id : 'Please select a location first',
    }));
  };

  const handleAreaChange = (e, isValid = true) => {
    onAreaChange?.(e.target.value);
    onChange(e);
    setIsAreaValid(isValid);

    if (isValid && e.target.value) {
      setErrors((prev) => ({ ...prev, area_id: '' }));
    }
  };

  const handleAreaValidationChange = (isValid) => {
    setIsAreaValid(isValid);
    if (!isValid && formData.location_id && formData.area_id) {
      setErrors((prev) => ({ ...prev, area_id: 'Please select a valid area' }));
    } else {
      setErrors((prev) => ({ ...prev, area_id: '' }));
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    onChange(e);
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    // Required fields
    if (!formData.name?.trim()) newErrors.name = 'Equipment name is required';
    if (!formData.eq_category) newErrors.eq_category = 'Category is required';
    if (!formData.eq_quantity && formData.eq_quantity !== 0) newErrors.eq_quantity = 'Quantity is required';
    if (!formData.eq_limit && formData.eq_limit !== 0) newErrors.eq_limit = 'Limit is required';

    // Location/Area or Medical Center validation
    if (selectionType === 'location') {
      if (!formData.location_id || formData.location_id === '0') {
        newErrors.location_id = 'Please select a location';
      }
      if (formData.location_id && (!formData.area_id || !isAreaValid)) {
        newErrors.area_id = 'Please select a valid area';
      }
    } else {
      if (!formData.medical_center_id || formData.medical_center_id === '0') {
        newErrors.medical_center_id = 'Please select a medical center';
      }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // Normalize data before saving
    const normalizedData = {
      ...formData,
      location_id: selectionType === 'medical' ? 0 : (formData.location_id || 0),
      area_id: selectionType === 'medical' ? 0 : (formData.area_id || 0),
      medical_center_id: selectionType === 'location' ? 0 : (formData.medical_center_id || 0),
      user_id: userId,
    };

    onSave(normalizedData);
  };

  const handleClose = () => {
    sessionStorage.removeItem('area_id');
    sessionStorage.removeItem('location_id');
    onClose();
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
      tabIndex="-1"
      aria-modal="true"
      role="dialog"
    >
      <div className="modal-dialog modal-dialog-scrollable modal-lg">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header d-flex align-items-center">
              <h5 className="modal-title">
                {isEditMode ? 'Edit Equipment' : 'Add Equipment'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                aria-label="Close"
                disabled={isLoading}
              />
            </div>

            <div className="modal-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name || ''}
                    onChange={handleChange}
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    disabled={isLoading}
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Category <span className="text-danger">*</span>
                  </label>
                  <select
                    name="eq_category"
                    value={formData.eq_category || ''}
                    onChange={handleChange}
                    className={`form-select ${errors.eq_category ? 'is-invalid' : ''}`}
                    disabled={isLoading}
                  >
                    <option value="">Select Category</option>
                    <option value="Device">Device</option>
                    <option value="Consumables">Consumables</option>
                  </select>
                  {errors.eq_category && <div className="invalid-feedback">{errors.eq_category}</div>}
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Quantity <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    name="eq_quantity"
                    value={formData.eq_quantity || ''}
                    onChange={handleChange}
                    className={`form-control ${errors.eq_quantity ? 'is-invalid' : ''}`}
                    disabled={isLoading}
                  />
                  {errors.eq_quantity && <div className="invalid-feedback">{errors.eq_quantity}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Limit <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    name="eq_limit"
                    value={formData.eq_limit || ''}
                    onChange={handleChange}
                    className={`form-control ${errors.eq_limit ? 'is-invalid' : ''}`}
                    disabled={isLoading}
                  />
                  {errors.eq_limit && <div className="invalid-feedback">{errors.eq_limit}</div>}
                </div>
              </div>

              {/* Selection Type */}
              <div className="row mb-3">
                <div className="col-12">
                  <label className="me-3">
                    {forceMedicalCenterOnly || isSpecificMedicalCenter
                      ? 'Medical Center'
                      : 'Choose Location or Medical Center'} <span className="text-danger">*</span>
                  </label>

                  {!forceMedicalCenterOnly && !isSpecificMedicalCenter && (
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="selectionType"
                        id="locationRadio"
                        value="location"
                        checked={selectionType === 'location'}
                        onChange={handleSelectionTypeChange}
                        disabled={isLoading}
                      />
                      <label className="form-check-label" htmlFor="locationRadio">
                        Location
                      </label>
                    </div>
                  )}

                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="selectionType"
                      id="medicalRadio"
                      value="medical"
                      checked={selectionType === 'medical'}
                      onChange={handleSelectionTypeChange}
                      disabled={isLoading || forceMedicalCenterOnly || isSpecificMedicalCenter}
                    />
                    <label className="form-check-label" htmlFor="medicalRadio">
                      Medical Center
                    </label>
                  </div>
                </div>
              </div>

              {/* Conditional Fields */}
              {selectionType === 'location' ? (
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Location <span className="text-danger">*</span>
                    </label>
                    <LocationDropdown
                      value={formData.location_id || ''}
                      onChange={handleLocationChange}
                      className={errors.location_id ? 'is-invalid' : ''}
                      disabled={isLoading}
                    />
                    {errors.location_id && <div className="text-danger">{errors.location_id}</div>}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Area <span className="text-danger">*</span>
                    </label>
                    <AreaDropdown
                      locationId={formData.location_id}
                      value={formData.area_id || ''}
                      onChange={handleAreaChange}
                      onValidationChange={handleAreaValidationChange}
                      className={errors.area_id ? 'is-invalid' : ''}
                      disabled={!formData.location_id || isLoading}
                    />
                    {errors.area_id && <div className="text-danger">{errors.area_id}</div>}
                  </div>
                </div>
              ) : (
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Medical Center <span className="text-danger">*</span>
                    </label>
                    <MedicalDropdown
                      value={
                        formData.medical_center_id ||
                        (isSpecificMedicalCenter ? sessionMedicalCenterId : '')
                      }
                      onChange={(e) => {
                        onChange(e);
                        setErrors((prev) => ({ ...prev, medical_center_id: '' }));
                      }}
                      className={errors.medical_center_id ? 'is-invalid' : ''}
                      disabled={isLoading || isSpecificMedicalCenter}
                    />
                    {errors.medical_center_id && (
                      <div className="text-danger">{errors.medical_center_id}</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    {isEditMode ? 'Updating...' : 'Saving...'}
                  </>
                ) : isEditMode ? (
                  'Update Equipment'
                ) : (
                  'Save Equipment'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EquipmentAddModal;